import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStaticIP, clearStaticIP } from './HelperFunctions';
import { clearSeasonList } from './services/seasonList';
import { clearRememberedPeriod } from './services/selectedPeriod';
import { clearMenus } from './services/menuList';
import { notify } from './SnackbarContext';

const UserContext = createContext();

const UserProvider = ({ children }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState(sessionStorage.getItem('username') || null);
  const [isLoggedIn, setIsLoggedIn] = useState(sessionStorage.getItem('isLoggedIn') === 'true');
  const [unitCode, setUnitCode] = useState(sessionStorage.getItem('unitCode') || null);
  const [operatorCode, setOperatorCode] = useState(sessionStorage.getItem('operatorCode') || null);
  const [serverIp, setServerIp] = useState(sessionStorage.getItem('serverIp') || null);
  const [clientName, setClientName] = useState(sessionStorage.getItem('clientName') || null);

  useEffect(() => {
    const storedUsername = sessionStorage.getItem('username');
    const storedIsLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const storedUnitCode = sessionStorage.getItem('unitCode');
    const storedOperatorCode = sessionStorage.getItem('operatorCode');
    const storedServerIp = sessionStorage.getItem('serverIp');
    const storedClientName = sessionStorage.getItem('clientName');

    if (storedUsername && storedIsLoggedIn) {
      setUsername(storedUsername);
      setIsLoggedIn(storedIsLoggedIn);
      setUnitCode(storedUnitCode);
      setOperatorCode(storedOperatorCode);
      setServerIp(storedServerIp);
      setClientName(storedClientName);
    }

    let timer;
    const resetTimeout = () => {
      clearTimeout(timer);
      timer = setTimeout(handleTimeout, 30 * 60 * 1000); // 30 minutes of inactivity
    };

    const handleTimeout = () => {
      if (sessionStorage.getItem('isLoggedIn') !== 'true') return; // already on the login page
      notify.warning('Session timed out due to inactivity. You have been logged out.');
      logout();
      navigate('/login');
    };

    window.addEventListener('mousemove', resetTimeout);
    window.addEventListener('keypress', resetTimeout);

    resetTimeout();

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', resetTimeout);
      window.removeEventListener('keypress', resetTimeout);
    };
  }, [navigate]);

  const login = (username, unitCode, operatorCode, serverIp, clientName) => {
    setUsername(username);
    setIsLoggedIn(true);
    setUnitCode(unitCode);
    setOperatorCode(operatorCode);
    setServerIp(clientName);
    setClientName(serverIp);

    sessionStorage.setItem('username', username);
    sessionStorage.setItem('isLoggedIn', 'true');
    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('operatorCode', operatorCode);
    sessionStorage.setItem('serverIp', serverIp);
    sessionStorage.setItem('clientName', clientName);

    // Look up the public IP once for this login (used by every save).
    clearStaticIP();
    getStaticIP();
  };

  const logout = () => {
    setUsername(null);
    setIsLoggedIn(false);
    setUnitCode(null);
    setOperatorCode(null);
    setServerIp(null);
    setClientName(null);
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('isLoggedIn');
    sessionStorage.removeItem('unitCode');
    sessionStorage.removeItem('operatorCode');
    sessionStorage.removeItem('serverIp');
    sessionStorage.removeItem('clientName');

    // Cached per login; fetched again after the next login.
    clearSeasonList();
    clearStaticIP();
    clearRememberedPeriod();
    clearMenus();
  };

  return (
    <UserContext.Provider value={{ username, isLoggedIn, unitCode, operatorCode, serverIp, clientName, login, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };
